import { useCallback } from 'react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useControllableState } from '../../../../hooks/useControllableState';
import { Pagination, Sorting } from '../../../../types';
import { DEFAULT_PAGINATION, DEFAULT_SORTING } from '../../constants/defaults';
import { TableProps } from '../../types/TableProps';
import { useTablePagination } from '../useTablePagination/useTablePagination';
import { useTableSorting } from '../useTableSorting/useTableSorting';

export const useTable = <D extends Record<string, unknown>>({
  columns,
  data,
  manualPagination: manualPaginationProp = true,
  manualSorting: manualSortingProp = true,
  meta,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
}: {
  columns: TableProps<D>['columns'];
  data: TableProps<D>['data'];
  manualPagination?: boolean;
  manualSorting?: boolean;
  meta?: TableProps<D>['meta'];
  pagination?: TableProps<D>['pagination'];
  onPaginationChange?: TableProps<D>['onPaginationChange'];
  sorting?: TableProps<D>['sorting'];
  onSortingChange?: TableProps<D>['onSortingChange'];
}) => {
  const [sortingValue, setSortingValue, isSortingControlled] =
    useControllableState<Sorting>({
      value: sorting,
      onChange: onSortingChange,
      initialInternalValue: DEFAULT_SORTING,
    });

  const { sortingState, onSortingStateChange } = useTableSorting(
    sortingValue,
    setSortingValue,
  );

  const [paginationValue, setPaginationValue, isPaginationControlled] =
    useControllableState<Pagination>({
      value: pagination,
      onChange: onPaginationChange,
      initialInternalValue: DEFAULT_PAGINATION,
    });

  const { paginationState, onPaginationStateChange } = useTablePagination(
    paginationValue,
    setPaginationValue,
  );

  const { getHeaderGroups, getRowModel, setPageIndex } = useReactTable<D>({
    autoResetPageIndex: false,
    columns,
    data,
    state: {
      pagination: isPaginationControlled ? paginationState : undefined,
      sorting: isSortingControlled ? sortingState : undefined,
    },
    manualPagination: manualPaginationProp,
    manualSorting: manualSortingProp,
    meta,
    pageCount: meta?.lastPage,
    onPaginationChange: isPaginationControlled
      ? onPaginationStateChange
      : undefined,
    onSortingChange: isSortingControlled ? onSortingStateChange : undefined,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const numberOfCols = getHeaderGroups()[0]?.headers.length ?? 0;

  const setPage = useCallback(
    (page: number) => {
      setPageIndex(Math.max(0, page - 1));
    },
    [setPageIndex],
  );

  return {
    pagination,
    getHeaderGroups,
    getRowModel,
    numberOfCols,
    setPage,
  };
};
